/**
 * MarkdownContent — renders markdown string as themed HTML
 *
 * Uses `marked` to parse and the `md-chat` Tailwind preset for styling.
 * Workspace file references are enriched into rich renders
 * (see docs/plans/2026-07-04_CHAT_UI_REDESIGN.md §3 — three tiers):
 *
 *   [shortname](/papan/path)  → FileChip pill (icon + shortname, click-to-open)
 *   ![caption](/papan/path)   → inline image via the streaming content URL
 *   bare /papan/... path      → dotted-underline link (legacy tier, kept)
 *
 * Enrichment happens at the DOM level (detached <template>), NOT via string
 * replace on the HTML — a string-level pass can't tell a path inside an
 * `href="..."` attribute from one in text, so it would corrupt markdown-
 * authored links (nested anchors, anchors inside attributes).
 */
import { useCallback, useState, useRef, useEffect } from 'react'
import { marked } from 'marked'
import { filesApi, miniappApi } from '@papan/files/client'
import { isSessionEndedError, currentSessionSignal } from '@papan/api-client'
import { onSessionEnd } from '@papan/core-ui/ui/sessionEnded'
import { driveFileIO } from '@papan/files/client/file-io'
import { renderMiniappIconSvg, ensureMiniappIconAssets, useToastOptional } from '@papan/core-ui/ui'
import { isMiniappFolder, type MiniappIconSpec } from '@papan/types'
// Direct subpath import (not via the `@papan/collab/ui` barrel) — the
// barrel loads MarkdownEditor.tsx which calls `marked.use(...)` at module
// load time. Tests that mock `marked` as `{ parse }` (no `.use`) would
// crash on barrel resolution; the dedicated subpath export sidesteps it.
import { sanitizeMarkdownHtml } from '@papan/collab/ui/sanitize-markdown'
import { chipIconSvg, chipDisplayName } from './chipIcon.js'

// #208 (@papan-only — see features/agent/SYNC.md:524, the
// marked-vs-tree-sitter intentional UI divergence). The streaming render
// path feeds `content` a new value every token; re-parsing the ENTIRE
// accumulated markdown per token is O(n²) and replaces the whole DOM
// subtree each time — pathological for very large messages (skill dumps,
// long reports) and the root of the agent-skill-gating stagetest settle
// flake. We throttle the re-parse to at most once per interval but
// ALWAYS parse the final (trailing) value, so the settled DOM is
// byte-identical to the un-throttled output (synced contract preserved;
// only the intermediate streaming cadence changes).
const STREAM_PARSE_INTERVAL_MS = 120
// Per-frame parse cost = marked.parse + DOMPurify sanitize + linkify, and it
// grows with message size (DOMPurify dominates: ~120ms on a 100K message,
// ~12x marked alone). For very large streaming messages (skill dumps, long
// reports) a single parse can EXCEED the 120ms base interval, so the throttle
// never leaves the main thread idle and the streamed DOM never settles — the
// deterministic agent-skill-gating stagetest timeout. Back the interval off to
// a multiple of the LAST measured parse so there's always an idle gap to render
// and settle in. The 120ms floor keeps small/normal messages responsive; the
// cap bounds the trailing-edge latency. The trailing-edge guarantee below still
// parses the final value, so the settled output stays byte-identical (#208).
const PARSE_INTERVAL_COST_FACTOR = 2.5
const MAX_PARSE_INTERVAL_MS = 1500
const nowMs = (): number =>
  (typeof performance !== 'undefined' && typeof performance.now === 'function')
    ? performance.now()
    : Date.now()

interface Props {
  content: string
  className?: string
  /** Called when a /papan/ path link is clicked with the resolved file info; `at.line` is the
   *  link's `:line` suffix (#249), for the host to open the file at that line. */
  onPathClick?: (fileId: string, isFolder: boolean, path: string, at?: { line?: number }) => void
}

/** Normalize an href/src candidate to a canonical /papan/ path, or null
 *  if it doesn't point into the workspace VFS. Accepts `/papan/...`,
 *  `_system/...` and `/_system/...` (same set the bare-path tier matches). */
function normalizePapanRef(raw: string): string | null {
  const base = peelLineSuffix(raw.trim()).base
  // Markdown-authored link hrefs are URL-encoded (the model emits
  // `/papan/Papan%20AI.workspace/...`); decode so the path matches the real
  // folder name before it reaches filesApi.resolvePath. `decodeURI` keeps
  // path separators intact and no-ops on already-plain bare-path text; a
  // malformed sequence falls back to the raw value.
  let decoded = base
  try { decoded = decodeURI(base) } catch { /* malformed % escape — keep raw */ }
  if (decoded.startsWith('/papan/')) return decoded
  if (/^\/?_system\//.test(decoded)) return '/papan/' + decoded.replace(/^\//, '')
  return null
}

/** Peel an optional trailing `:<line>` off a workspace ref (e.g.
 *  `/papan/src/app.py:42` → base `/papan/src/app.py`, line `42`). The line is
 *  kept in `data-papan-line` (hidden from display) and a click opens the file
 *  at that line (#249). */
function peelLineSuffix(raw: string): { base: string; line: string | null } {
  const m = /:(\d+)$/.exec(raw)
  return m ? { base: raw.slice(0, m.index), line: m[1] } : { base: raw, line: null }
}

/** Strip trailing sentence punctuation from a matched bare path. */
function stripTrailingPunct(path: string): string {
  return path.replace(/[\s,;:]+$/, '').replace(/\.(?!\w)$/, '')
}

// Per-type chip icons, inlined — chips are built inside a detached template
// (post-sanitize, our own static markup), where React components can't reach.
// Shared with FilePill (the React twin) via chipIcon.ts so both look alike.

/** Turn an existing <a> (markdown-authored link to a workspace path) into a
 *  FileChip: icon + shortname pill. Keeps the author's label; if the label
 *  is just the raw path, display the basename instead (full path in title). */
function upgradeAnchorToChip(a: HTMLAnchorElement, path: string, line: string | null) {
  a.removeAttribute('href')
  a.setAttribute('data-papan-path', path)
  if (line) a.setAttribute('data-papan-line', line)
  a.setAttribute('title', line ? `${path}:${line}` : path)
  a.className = 'papan-file-chip'
  const label = (a.textContent || '').trim()
  // Author's label wins, unless it's just the raw path or the bare
  // `<name>.miniapp` folder name — then show the clean display name.
  const display = (!label || normalizePapanRef(label) === path || label === path.split('/').filter(Boolean).pop())
    ? chipDisplayName(path)
    : label
  const isApp = isMiniappFolder(path.split('/').filter(Boolean).pop() || '')
  // For a `.miniapp` chip whose icon spec is ALREADY resolved (module cache), emit
  // the app's REAL launcher icon as a STATIC SVG STRING — part of the parsed HTML,
  // so streaming re-parses re-emit the identical markup (no root to wipe, no
  // flicker). Until it resolves (or for a non-app), emit the placeholder glyph;
  // `data-papan-app` lets the resolve-then-rerender effect find + resolve it, after
  // which the next parse emits the real icon here.
  const resolved = isApp ? appIconResolved.get(path) : undefined
  // Wrap the resolved 22px launcher icon in the same padded slot the old imperative
  // mount used (padding-block:3px) — keeps the app chip's vertical rhythm the user
  // dialed in, and matches the placeholder→resolved height so there's no jump.
  const iconHtml = resolved
    ? `<span class="papan-app-chip-slot" style="display:inline-flex;flex:none;line-height:0;padding-block:3px">${renderMiniappIconSvg({ spec: resolved.spec, name: resolved.name, size: 22 })}</span>`
    : chipIconSvg(path)
  a.innerHTML = `${iconHtml}<span>${escapeHtml(display)}</span>`
  if (isApp) a.setAttribute('data-papan-app', path)
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')
}

/**
 * Bare-path tier — convert /papan/... paths in TEXT nodes to clickable links.
 *
 * Known edge cases (tested & fixed in the string-replace era — preserve):
 * - Dots in filenames: ".workpapan", ".html" must not be treated as sentence-ending
 * - _system/ paths: may appear without /papan/ prefix, must be normalized
 * - Spaces & ampersands: "Staff Cost & PnL 2025.xlsx" (DOM text nodes are
 *   already entity-decoded, so plain `&` — no &amp; handling needed here)
 * - Quotes: model outputs "/papan/file.xlsx" — quotes must not be captured
 * - Code spans: `<code>/papan/...</code>` → the anchor wraps the code element
 * - Trailing punctuation: path at end of sentence "/papan/file.xlsx."
 */
// Trailing `(:\d+)?` optionally captures a `:line` suffix (group 2) so it is
// consumed by the match and hidden from display, not left dangling as text.
const BARE_PATH_RE = /(\/papan\/[\w./\- ()+@#,&]+|\/?\b_system\/[\w./\- ()+@#,&]+)(:\d+)?/g

function makePathLink(doc: Document, path: string, display: Node, line?: string | null): HTMLAnchorElement {
  const a = doc.createElement('a')
  a.href = '#'
  a.className = 'papan-path-link'
  a.setAttribute('data-papan-path', path)
  if (line) a.setAttribute('data-papan-line', line)
  a.setAttribute('style', 'text-decoration:underline;text-decoration-style:dotted;text-underline-offset:2px;cursor:pointer;color:inherit')
  a.appendChild(display)
  return a
}

function linkifyTextNode(node: Text) {
  const doc = node.ownerDocument
  const text = node.data
  BARE_PATH_RE.lastIndex = 0
  if (!BARE_PATH_RE.test(text)) return
  BARE_PATH_RE.lastIndex = 0

  const frag = doc.createDocumentFragment()
  let last = 0
  for (let m = BARE_PATH_RE.exec(text); m; m = BARE_PATH_RE.exec(text)) {
    const raw = m[1]
    const lineSuffix = m[2] || '' // ':42' or ''
    const line = lineSuffix ? lineSuffix.slice(1) : null
    const clean = stripTrailingPunct(raw.replace(/^["']+|["']+$/g, ''))
    const path = normalizePapanRef(clean)
    if (!path) continue
    if (m.index > last) frag.appendChild(doc.createTextNode(text.slice(last, m.index)))
    const displayText = stripTrailingPunct(raw)
    frag.appendChild(makePathLink(doc, path, doc.createTextNode(displayText), line))
    // Advance past the path AND the (hidden) :line suffix so it isn't re-emitted
    // as trailing text. When trailing punct was stripped from displayText, the
    // difference falls through to the next slice as text (existing behavior).
    last = m.index + displayText.length + (displayText === raw ? lineSuffix.length : 0)
  }
  if (last === 0) return
  if (last < text.length) frag.appendChild(doc.createTextNode(text.slice(last)))
  node.replaceWith(frag)
}

/** Enrich workspace file references inside sanitized markdown HTML. */
function enrichPapanRefs(root: ParentNode & Node) {
  // Tier 1 — markdown-authored links → FileChips
  root.querySelectorAll('a[href]').forEach(el => {
    const a = el as HTMLAnchorElement
    const href = a.getAttribute('href') || ''
    const path = normalizePapanRef(href)
    if (path) {
      upgradeAnchorToChip(a, path, peelLineSuffix(href.trim()).line)
    } else if (/^https?:\/\//i.test(href.trim())) {
      // External link — open in a new tab so a click never navigates the SPA
      // (and the chat) away. noopener/noreferrer: standard target=_blank hygiene.
      a.setAttribute('target', '_blank')
      a.setAttribute('rel', 'noopener noreferrer')
    }
  })

  // Tier 2 — markdown-authored images → inline images via the streaming URL.
  // The resolved src is BAKED INTO THE HTML here (read synchronously from
  // `imgResolveResolved`), like the app-icon chip below — NOT patched onto a
  // live DOM node, which a re-commit wipes and never restores (#520). Three
  // states: resolved → emit the streaming src (+ crossorigin, #410); resolved
  // to null → degrade to the alt-text caption; not-yet-resolved → strip the raw
  // (unfetchable) path to `data-papan-src` and let the resolve-then-reparse
  // effect fill it in on the next parse.
  root.querySelectorAll('img[src]').forEach(el => {
    const img = el as HTMLImageElement
    const path = normalizePapanRef(img.getAttribute('src') || '')
    if (!path) return
    img.removeAttribute('src')
    img.className = 'papan-inline-img'
    img.setAttribute('data-papan-path', path)
    if (imgResolveResolved.has(path)) {
      const resolved = imgResolveResolved.get(path) ?? null
      if (resolved) {
        // crossorigin BEFORE src in the emitted markup — an <img> with no
        // crossorigin loads no-cors, which the API's CORP:same-origin blocks
        // once the SPA and API are separate origins (#410). Both attributes are
        // present when the browser parses this innerHTML, so order is moot here.
        const crossOrigin = driveFileIO.streamingCrossOrigin()
        if (crossOrigin) img.setAttribute('crossorigin', crossOrigin)
        img.setAttribute('src', driveFileIO.getStreamingUrl(resolved.id))
      } else {
        // Unresolvable (hallucinated path, deleted file) — muted caption, not a
        // broken-image glyph.
        const span = img.ownerDocument.createElement('span')
        span.className = 'papan-inline-img-missing'
        span.textContent = img.getAttribute('alt') || path
        img.replaceWith(span)
      }
    } else {
      img.setAttribute('data-papan-src', path)
    }
  })

  // Tier 3 — bare paths in text. Whole-path <code> spans get the anchor
  // wrapped AROUND the code element (string-era behavior); other text nodes
  // are split in place. Skip text already inside an anchor (incl. the chips
  // made above) and inside <pre> blocks.
  const doc = (root.ownerDocument || (root as unknown as Document))
  const codes: HTMLElement[] = []
  root.querySelectorAll('code').forEach(el => {
    const code = el as HTMLElement
    if (code.closest('pre') || code.closest('a')) return
    const path = normalizePapanRef(stripTrailingPunct((code.textContent || '').replace(/^["']+|["']+$/g, '')))
    if (path) codes.push(code)
  })
  for (const code of codes) {
    const cleaned = stripTrailingPunct((code.textContent || '').replace(/^["']+|["']+$/g, ''))
    const path = normalizePapanRef(cleaned)!
    const line = peelLineSuffix(cleaned).line
    const a = makePathLink(doc, path, doc.createTextNode(''), line)
    code.replaceWith(a)
    a.replaceChildren(code)
  }

  // Manual recursion instead of createTreeWalker — happy-dom (the test
  // environment) doesn't traverse template fragments with a TreeWalker.
  const textNodes: Text[] = []
  const collect = (node: Node) => {
    if (node.nodeType === 3 /* TEXT_NODE */) {
      // No parentElement = top-level text directly under the fragment —
      // still linkifiable (only skip when nested inside a/pre/code).
      const parent = (node as Text).parentElement
      if (!parent || !parent.closest('a, pre, code')) textNodes.push(node as Text)
      return
    }
    node.childNodes.forEach(collect)
  }
  collect(root as Node)
  textNodes.forEach(linkifyTextNode)
}

// A workspace path with a SPACE breaks CommonMark link parsing: a literal space
// in an unbracketed link destination is illegal, so `marked` drops the whole
// `[label](…)` (or `![alt](…)`) to raw text and no chip/inline-image is made —
// the ref renders as `[All Races.xlsx](…/All%20Races.xlsx)`, unclickable (seen
// on a `Boss View CRM.pdapp` workspace when the model half-encodes the path).
// Percent-encode ONLY spaces inside the PATH of a `/papan` / `_system` link
// destination so marked parses it; existing %20 is untouched (we replace spaces,
// never run encodeURI, which would double-encode `%`), and normalizePapanRef's
// decodeURI downstream recovers the real name.
//
// Deliberately narrow, because this is a string pass over raw markdown and the
// file's header explains enrichment is otherwise done at the DOM level to avoid
// exactly this class of corruption. Guards keep it from misfiring:
//   1. only the `](…)` destination, and only workspace roots;
//   2. an optional markdown TITLE (`… "t"` / `… 't'`) is peeled off and left
//      intact — folding it into the href would break a titled link even when the
//      path has no spaces;
//   3. a stray `%` (one not already a valid %XX escape) is encoded to %25 BEFORE
//      spaces, so the result is a valid URI that normalizePapanRef's decodeURI
//      can round-trip — otherwise `50% off.xlsx` → `50%%20off.xlsx` throws and
//      the chip resolves to a wrong path;
//   4. code contexts are skipped — fenced (matched by the SAME marker that
//      opened, so a `~~~` inside a ```-block doesn't end it), indented
//      (4-space / tab), and inline backtick spans — so a path shown AS CODE is
//      never rewritten.
//
// Two inherent blind spots of a line-based string pass, accepted as known
// (both are already broken by the raw space BEFORE this runs, so neither is a
// regression — this simply doesn't rescue them): a path containing a literal
// `)` (`/papan/(draft) My File.xlsx`) truncates at the first `)`; and a 4-space
// list-item CONTINUATION line is indistinguishable from an indented code block,
// so a spaced link nested under a list stays raw. A parser-level fix (a marked
// tokenizer) would cover both; not worth the global-marked blast radius here.
const WORKSPACE_LINK_DEST = /(\]\()((?:\/papan\/|\/?_system\/)[^)]*)(\))/g
const HAS_WORKSPACE_LINK = /\]\(\s*(?:\/papan\/|\/?_system\/)/
function encodeWorkspaceLinkSpaces(md: string): string {
  // Early-out: no workspace-link destination anywhere → nothing to do, skip the
  // whole per-line pass. This keeps the common case (prose, code, no `/papan`
  // link) off the O(n²)-sensitive streaming re-parse path (#208).
  if (!HAS_WORKSPACE_LINK.test(md)) return md
  const encodeDest = (_all: string, open: string, dest: string, close: string): string => {
    // Peel a trailing title (whitespace + quoted string); encode the path half
    // only, so the title keeps its own spaces and marked still parses dest+title.
    const m = /^([\s\S]*?)(\s+(?:"[^"]*"|'[^']*'))\s*$/.exec(dest)
    const rawPath = m ? m[1] : dest
    const title = m ? m[2] : ''
    const path = rawPath
      .replace(/%(?![0-9A-Fa-f]{2})/g, '%25') // stray % → %25 (keep valid %XX like %20)
      .replace(/ /g, '%20')
    return open + path + title + close
  }
  // The full run that opened the current fence — a close must be the same marker
  // AND at least as long (CommonMark), so a shorter same-char run inside a longer
  // fence does not end it.
  let fence: string | null = null
  return md.split('\n').map(line => {
    const fenceMatch = /^\s*(`{3,}|~{3,})/.exec(line)
    if (fenceMatch) {
      const run = fenceMatch[1]
      if (fence === null) fence = run
      else if (run[0] === fence[0] && run.length >= fence.length) fence = null
      return line
    }
    if (fence !== null) return line
    if (/^(?: {4,}|\t)/.test(line)) return line // indented code block
    // Split on inline-code spans; only the non-code segments are rewritten.
    return line.split(/(`+[^`]*`+)/).map(seg =>
      seg.startsWith('`') ? seg : seg.replace(WORKSPACE_LINK_DEST, encodeDest),
    ).join('')
  }).join('\n')
}

function parseMarkdown(content: string): string {
  try {
    // Sanitize marked output before downstream enrichment. marked
    // v17 passes script tags, javascript: hrefs, and inline event
    // handlers through unchanged — LLM output under prompt injection
    // can produce dangerous HTML. See sanitize-markdown.ts.
    // Enrichment runs AFTER sanitize on a detached template, and only
    // injects our own static markup with escaped attribute/text values.
    const raw = sanitizeMarkdownHtml(marked.parse(encodeWorkspaceLinkSpaces(content), { async: false, breaks: true }) as string)
    const tpl = document.createElement('template')
    tpl.innerHTML = raw
    enrichPapanRefs(tpl.content)
    return tpl.innerHTML
  } catch {
    return ''
  }
}

// ─── Async image resolution ───────────────────────────────────────────
// path → resolved file id (or null). Module-level so streaming re-parses
// (which replace the DOM subtree) don't re-hit the API; the browser HTTP
// cache handles the image bytes themselves.
type ImgResolve = { id: string } | null
const imgResolveCache = new Map<string, Promise<ImgResolve>>()
// Synchronously-available once the fetch settles — the inline-image twin of
// `appIconResolved` below, and for the identical reason. The resolved streaming
// `src` is BAKED INTO THE HTML STRING by enrichPapanRefs (read from this map),
// never patched imperatively onto a live DOM node: an imperative `src` is wiped
// by any re-commit of dangerouslySetInnerHTML, and the post-commit effect only
// re-runs on an html change — so a re-commit that keeps the same html left the
// image PERMANENTLY src-less, a broken glyph (#520). Baking means every commit
// re-emits the src, so it always sticks. A resolve settling bumps `resolveTick`
// to force one re-parse that emits the now-resolved src.
const imgResolveResolved = new Map<string, ImgResolve>()

function resolvePapanImage(path: string): Promise<ImgResolve> {   // one argument: it is passed to `.map`
  let p = imgResolveCache.get(path)
  if (!p) {
    const askedIn = currentSessionSignal()
    p = filesApi.resolvePath(path)
      .then(r => (r && !r.file.isFolder ? { id: r.id } : null))
      // Publish to the synchronous map on settle, so the next parse bakes the
      // resolved src straight into the HTML — unless the session it was asked in has
      // ended (a drop, or an answer settling after the end): that is not this
      // session's answer (#1316 — the caches are cleared at every session end, below).
      .then(
        res => { if (!askedIn?.aborted) imgResolveResolved.set(path, res); return res },
        e => { if (!askedIn?.aborted && !isSessionEndedError(e)) imgResolveResolved.set(path, null); return null },
      )
    imgResolveCache.set(path, p)
  }
  return p
}

// ─── Async app-icon resolution (miniapp chips → real launcher icon) ────
// path → { folder name, icon spec } (or null). Module-level so streaming
// re-parses (which replace the DOM subtree) don't re-hit the API.
type AppIcon = { name: string; spec: MiniappIconSpec | null } | null
const appIconCache = new Map<string, Promise<AppIcon>>()
// Synchronously-available once the fetch settles. This is what lets the FINAL
// re-committed chip mount its icon with NO async gap: the streaming/StrictMode
// re-renders replace the chip's DOM on each commit, so an async mount races the
// next commit and loses (`!isConnected`). Once resolved here, every later commit
// mounts synchronously into the fresh chip and sticks.
const appIconResolved = new Map<string, AppIcon>()

function resolveAppIcon(path: string): Promise<AppIcon> {   // one argument: it is passed to `.map`
  let p = appIconCache.get(path)
  if (!p) {
    const askedIn = currentSessionSignal()   // the WHOLE chain (resolve → icon → assets) belongs to this session
    p = filesApi.resolvePath(path)
      .then(async r => {
        if (!r || !r.file.isFolder) return null
        const { icon } = await miniappApi.getIcon(r.id)
        // Pre-resolve the assets the STRING renderer reads synchronously (a
        // custom Lucide icon's geometry, an emoji face) BEFORE we publish to
        // `appIconResolved`, so the very next parse emits the real icon at full
        // fidelity instead of the default box.
        await ensureMiniappIconAssets(icon, r.file.name)
        return { name: r.file.name, spec: icon }
      })
      .then(
        res => { if (!askedIn?.aborted) appIconResolved.set(path, res); return res },
        e => { if (!askedIn?.aborted && !isSessionEndedError(e)) appIconResolved.set(path, null); return null },
      )
    appIconCache.set(path, p)
  }
  return p
}

/** #1316 — these four caches belong to ONE session: a session end (another account, tenant or identity, leaving the
 *  share view) clears them, so the next session resolves every path as itself, and nothing an ended session asked is
 *  published (above). The listener only clears — it sends nothing (a request from a session-end listener would go out
 *  before the switch applied its headers); the next parse or mount asks fresh. */
onSessionEnd(() => { imgResolveCache.clear(); imgResolveResolved.clear(); appIconCache.clear(); appIconResolved.clear() })

export function MarkdownContent({ content, className, onPathClick }: Props) {
  // Parse the initial content synchronously so a *finalized* message
  // (content set once, never changes) renders immediately with zero
  // throttle latency and zero extra parses.
  const [html, setHtml] = useState<string>(() => parseMarkdown(content))
  // Bumped ONCE when a batch of async resolutions (miniapp icon specs AND inline
  // image ids) finishes, to force a single re-parse that now bakes the resolved
  // icon strings / image srcs into the HTML (see below).
  const [resolveTick, setResolveTick] = useState(0)
  // Optional: MarkdownContent is a leaf renderer that may mount outside the
  // app's global ToastProvider (unit tests, previews) — `null` there, and the
  // not-found feedback simply no-ops rather than crashing the render.
  const toast = useToastOptional()
  const containerRef = useRef<HTMLDivElement>(null)
  const lastParsedRef = useRef<string>(content)
  const lastParseAtRef = useRef<number>(nowMs())
  const lastParseMsRef = useRef<number>(0)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Always-current `content` for the icon-resolution re-parse, which must NOT
  // depend on `content` (that would re-parse every token, bypassing the throttle).
  const contentRef = useRef<string>(content)
  contentRef.current = content

  useEffect(() => {
    // Unchanged content (re-render from an unrelated parent state change,
    // e.g. the post-complete usage reload) → nothing to re-parse.
    if (content === lastParsedRef.current) return
    const commit = () => {
      timerRef.current = null
      lastParsedRef.current = content
      const t0 = nowMs()
      const parsed = parseMarkdown(content)
      lastParseMsRef.current = nowMs() - t0
      lastParseAtRef.current = nowMs()
      setHtml(parsed)
    }
    // Adaptive interval: never re-parse more often than ~2.5x the last parse
    // cost, so a heavy message (where parse > base interval) still leaves the
    // main thread idle enough to render and settle. Cheap messages keep the
    // 120ms floor (small content → ~0ms parse → floor).
    const interval = Math.min(
      MAX_PARSE_INTERVAL_MS,
      Math.max(STREAM_PARSE_INTERVAL_MS, Math.ceil(lastParseMsRef.current * PARSE_INTERVAL_COST_FACTOR)),
    )
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null }
    const since = nowMs() - lastParseAtRef.current
    if (since >= interval) {
      commit() // leading edge — first change after a quiet gap renders now
    } else {
      // Trailing edge: each new token clears the prior timer and reschedules,
      // so during a burst `marked.parse` runs at most once / interval and
      // the LAST value is always the one parsed (settled DOM == un-throttled).
      timerRef.current = setTimeout(commit, interval - since)
    }
    return () => { if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null } }
  }, [content])

  // After each html commit: resolve anything whose id/spec isn't cached yet —
  // inline images (path → file id → streaming URL) AND miniapp chip icons — then
  // bump `resolveTick` ONCE so parseMarkdown re-runs and BAKES the resolved src /
  // icon straight into the HTML string. Both are baked (never patched onto a live
  // node), so every later re-parse re-emits the SAME markup — flicker-free, and
  // immune to the re-commit that left an imperatively-patched image src-less
  // (#520). No work (and no tick) when everything is already resolved.
  useEffect(() => {
    const c = containerRef.current
    if (!c) return
    const unresolvedImgs = new Set<string>()
    c.querySelectorAll('img[data-papan-src]').forEach(im => {
      const path = im.getAttribute('data-papan-src')
      if (path && !imgResolveResolved.has(path)) unresolvedImgs.add(path)
    })
    const unresolvedIcons = new Set<string>()
    c.querySelectorAll<HTMLElement>('a.papan-file-chip[data-papan-app]').forEach(a => {
      const path = a.getAttribute('data-papan-app')
      if (path && !appIconResolved.has(path)) unresolvedIcons.add(path)
    })
    if (unresolvedImgs.size === 0 && unresolvedIcons.size === 0) return
    let alive = true
    const startedIn = currentSessionSignal()   // #1316: a batch from an ended session re-parses nothing (see above)
    // Images and icons resolve as SEPARATE batches so an image is baked as soon
    // as the image batch settles — it must not wait on a slow miniapp icon fetch
    // (the independence the removed per-image hydrateInlineImages had). Each
    // batch bumps once (a re-parse bakes everything resolved so far), which keeps
    // a chip-heavy message from triggering one re-parse per chip.
    const bump = () => { if (alive && !startedIn?.aborted) setResolveTick(t => t + 1) }
    if (unresolvedImgs.size > 0) void Promise.all([...unresolvedImgs].map(resolvePapanImage)).then(bump)
    if (unresolvedIcons.size > 0) void Promise.all([...unresolvedIcons].map(resolveAppIcon)).then(bump)
    return () => { alive = false }
  }, [html])

  // When a resolve batch settles, re-parse the CURRENT content once so images
  // and chips bake in their resolved src/icon. Depends ONLY on `resolveTick`
  // (reads content via ref) so it never fires on a plain token update — that
  // path stays throttled above.
  useEffect(() => {
    if (resolveTick === 0) return
    const latest = contentRef.current
    lastParsedRef.current = latest
    setHtml(parseMarkdown(latest))
  }, [resolveTick])

  const handleClick = useCallback(async (e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement
    const el = target.closest('a.papan-path-link, a.papan-file-chip, img[data-papan-path]') as HTMLElement | null
    if (!el) return

    e.preventDefault()
    const path = el.dataset.papanPath
    if (!path) return

    try {
      const result = await filesApi.resolvePath(path)
      if (result) {
        const line = Number(el.dataset.papanLine)
        onPathClick?.(result.id, result.file.isFolder, path, Number.isInteger(line) && line > 0 ? { line } : undefined)
      } else {
        // #662: `resolvePath` collapses a 404 to `null`, so a file chip whose
        // target was deleted/moved used to click into silence. Tell the user,
        // mirroring the host's own onOpenFile toast, instead of a dead click.
        toast?.error(`File not found: ${path}`)
      }
    } catch {
      toast?.error(`Could not open: ${path}`)
    }
  }, [onPathClick, toast])

  if (!html) return null

  return (
    <div
      ref={containerRef}
      className={`md-chat${className ? ` ${className}` : ''}`}
      dangerouslySetInnerHTML={{ __html: html }}
      onClick={handleClick}
    />
  )
}
